import { Css, Palette, StackBarGraph, TableSummary, pageContentPaddingX } from "@homebound/beam";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { hasPendingPlanPackageUpdates } from "src/routes/libraries/product-offerings/overview/PackageVersionStatus";
import { useProductOfferingStatusSummaryQuery } from "src/routes/libraries/product-offerings/overview/ProductOfferingStatusSummary.generated";
import { pluralize, queryResult } from "src/utils";

type ProductOfferingStatusSummaryProps = { projectId: string };

export function ProductOfferingStatusSummary(props: ProductOfferingStatusSummaryProps) {
  const { projectId } = props;
  const query = useProductOfferingStatusSummaryQuery({ variables: { projectId } });

  return queryResult(query, {
    // Rather than a spinner that may resolve to nothing
    showLoading: "never",
    data: ({ productOfferings: { entities } }) => {
      if (entities.isEmpty) return <></>;
      // Shared by the summary metrics and the bar graph
      const statusMetrics = [
        {
          label: "Published",
          count: entities.count((po) => po.effectiveStatus.code === ReadyPlanStatus.Active),
          icon: "checkCircle" as const,
          color: Palette.Green500,
        },
        {
          label: "In Draft",
          count: entities.count((po) => po.effectiveStatus.code === ReadyPlanStatus.Draft),
          icon: "errorCircle" as const,
          color: Palette.Gray500,
        },
        // TODO: Add the "Updating" segment once the BE has that status: https://linear.app/homebound-team/issue/PLAN-2035
      ];
      // Same rule as the table's yellow design package dots; an offering is counted once even if both packages match
      const needsReviewCount = entities.count(
        (po) =>
          hasPendingPlanPackageUpdates(po.interiorDesignPackage, po.planPackage.id) ||
          hasPendingPlanPackageUpdates(po.exteriorDesignPackage, po.planPackage.id),
      );
      return (
        <div css={{ ...pageContentPaddingX, ...Css.pt3.pb2.layoutContainer.$ }}>
          <TableSummary
            title="Product Offering Status"
            metrics={[
              ...statusMetrics,
              // The designs want them flagged for the summary, but their status (draft, published) to be whats tracked in the graph
              // So we spread them into the summary and exclude them from the graph
              ...(needsReviewCount > 0
                ? [
                    {
                      label: `${pluralize(needsReviewCount, "Offering")} Needing Design Review`,
                      count: needsReviewCount,
                      icon: "error" as const,
                      color: Palette.Yellow600,
                    },
                  ]
                : []),
            ]}
            footer={
              <StackBarGraph
                title="Offerings by Status"
                totalLabel={`${entities.length} ${pluralize(entities.length, "Offering")}`}
                segments={statusMetrics}
              />
            }
          />
        </div>
      );
    },
  });
}
